import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { BookFitCall } from "@/components/site/BookFitCall";
import { articleSchema, canonicalLinks, faqPageSchema, jsonLd, ogImage, ogUrl, breadcrumbSchema, webPageSchema, absoluteUrl, ORG_ID } from "@/lib/seo";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { seoTitleFor, seoDescriptionFor } from "@/data/postSeo";
import { publishedAt, modifiedAt, postDateByline } from "@/data/postDates";
import { postFaqs } from "@/data/postFaqs";
import { ArticleVideo } from "@/components/site/ArticleVideo";
import { PartnerRewardChart } from "@/components/diagrams/PartnerRewardChart";
import { ToolCallout, onArticleCalloutClick, withToolCallouts } from "@/components/site/tools/ToolCallout";
import { insightToolCallouts } from "@/data/tools";
import founderDeskAfterHoursImg from "@/assets/photos/founder-desk-after-hours.webp";
import shopCounterPriceBoardImg from "@/assets/photos/shop-counter-price-board.webp";
import teamBriefingImg from "@/assets/photos/team-briefing.webp";
import leadershipTableImg from "@/assets/photos/leadership-table.webp";
import siteOfficePlansImg from "@/assets/photos/site-office-plans.webp";
import { postVideos, videoCardUrl, videoEmbedUrl, videoFor } from "@/data/postVideos";

const leadPhotos: Record<string, string> = {
  "founder-desk-after-hours": founderDeskAfterHoursImg,
  "shop-counter-price-board": shopCounterPriceBoardImg,
  "team-briefing": teamBriefingImg,
  "leadership-table": leadershipTableImg,
  "site-office-plans": siteOfficePlansImg,
};

export const Route = createFileRoute("/insights/$slug")({
  loader: async ({ params }) => {
    const { getPost, posts } = await import("@/data/posts");
    const { withArticleTable } = await import("@/data/articleTables");
    const post = getPost(params.slug);
    if (!post) throw notFound();
    const i = posts.findIndex((p) => p.slug === post.slug);
    const related = [...posts.slice(i + 1), ...posts.slice(0, i)].slice(0, 3).map(({ html: _html, ...summary }) => summary);
    return { post: { ...post, html: withArticleTable(post.slug, post.html) }, related };
  },
  head: ({ loaderData }) => {
    const p = loaderData?.post;
    if (!p)
      return {
        meta: [
          { title: "Insight Not Found | Ajility" },
          { name: "robots", content: "noindex, follow" },
          { name: "description", content: "This insight could not be found. Browse Ajility's operating notes and case studies for founder-led Australian businesses." },
          { property: "og:title", content: "Insight Not Found | Ajility" },
          { property: "og:description", content: "This insight could not be found. Browse Ajility's operating notes and case studies." },
          ...ogImage("insights", "Ajility operating insights and case studies"),
          ogUrl("/insights"),
        ],
      };
    const metaTitle = seoTitleFor(p.slug, p.title);
    const metaDescription = seoDescriptionFor(p.slug, p.description);
    const video = postVideos[p.slug];
    return {
      meta: [
        { name: "twitter:card", content: "summary_large_image" },
        { title: metaTitle },
        { name: "description", content: metaDescription },
        { property: "og:title", content: metaTitle },
        { property: "og:description", content: metaDescription },
        { property: "og:type", content: "article" },
        { name: "twitter:title", content: metaTitle },
        { name: "twitter:description", content: metaDescription },
        ...ogImage(`insights-${p.slug}`, p.title),
        ogUrl(`/insights/${p.slug}`),
      ],
      links: canonicalLinks(`/insights/${p.slug}`),
      scripts: [
        jsonLd(breadcrumbSchema([{ name: "Insights", path: "/insights" }, { name: p.title, path: `/insights/${p.slug}` }])),
        jsonLd(
          { ...webPageSchema({
            path: `/insights/${p.slug}`,
            name: metaTitle,
            description: metaDescription,
            dateModified: modifiedAt(p.slug),
            mainEntityId: `${absoluteUrl(`/insights/${p.slug}`)}#article`,
            image: `insights-${p.slug}`,
          }), ...(p.slug === "working-partner-vs-silent-partner" ? { speakable: { "@type": "SpeakableSpecification", cssSelector: ["#short-answer"] } } : {}) },
        ),
        jsonLd(
          articleSchema({
            title: p.title,
            description: p.description,
            slug: p.slug,
            datePublished: publishedAt(p.slug),
            dateModified: modifiedAt(p.slug),
            section: p.category,
          }),
        ),
        ...(video
          ? [
              jsonLd({
                "@context": "https://schema.org",
                "@type": "VideoObject",
                "@id": `${absoluteUrl(`/insights/${p.slug}`)}#video`,
                name: video.title,
                description: p.description,
                thumbnailUrl: videoCardUrl(video.youtubeId),
                embedUrl: videoEmbedUrl(video.youtubeId),
                uploadDate: video.uploadDate,
                duration: video.durationIso,
                inLanguage: "en-AU",
                publisher: { "@id": ORG_ID },
              }),
            ]
          : []),
        ...(postFaqs[p.slug]
          ? [jsonLd(faqPageSchema(`/insights/${p.slug}`, postFaqs[p.slug] ?? [], `${absoluteUrl(`/insights/${p.slug}`)}#article`))]
          : []),
      ],
    };
  },
  component: InsightDetail,
  notFoundComponent: () => (
    <div className="mx-auto max-w-3xl px-5 pb-20 pt-40 text-center">
      <h1 className="font-serif text-4xl text-foreground">Insight not found</h1>
      <Link to="/insights" className="mt-6 inline-block text-sm text-midnight underline">
        Back to all insights
      </Link>
    </div>
  ),
});

function InsightDetail() {
  const { post, related } = Route.useLoaderData();
  const faqs = postFaqs[post.slug] ?? [];
  const video = videoFor(post.slug);
  // Keep stored article wrappers from closing the container before the appended questions.
  let openDivs = 0;
  let bodyHtml = post.html.replace(/<div\b[^>]*>|<\/div>/g, (tag) => {
    if (tag !== "</div>") { openDivs += 1; return tag; }
    if (openDivs === 0) return "";
    openDivs -= 1;
    return tag;
  });
  // The existing full-time comparison ends within its SVG caption.
  if (post.slug === "fractional-vs-fulltime") bodyHtml += "</text></svg></figure>";
  if (post.slug !== "working-partner-vs-silent-partner") bodyHtml = withToolCallouts(post.slug, bodyHtml);
  const inlineFaq = /<dl class="faq">[\s\S]*?<\/dl>/.exec(bodyHtml);
  const escapeHtml = (text: string) => text.replace(/[&<>"']/g, (character) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[character] ?? character));
  const existingFaqCount = inlineFaq ? (inlineFaq[0].match(/<dt>/g) ?? []).length : 0;
  const appendedFaqHtml = faqs.slice(existingFaqCount).map((faq) => `<dt>${escapeHtml(faq.q)}</dt><dd>${escapeHtml(faq.a)}</dd>`).join("");
  const faqHtml = (inlineFaq
    ? inlineFaq[0].replace("</dl>", `${appendedFaqHtml}</dl>`)
    : faqs.length ? `<dl class="faq">${appendedFaqHtml}</dl>` : "").replace(/<dt>([\s\S]*?)<\/dt>/g, "<dt><h3>$1</h3></dt>");
  const articleHtml = inlineFaq
    ? bodyHtml.replace(inlineFaq[0], faqHtml)
    : `${bodyHtml}${faqHtml ? '<p class="kicker">Questions founders ask</p><h2>Common questions</h2><div class="rule"></div>' + faqHtml : ""}`;

  return (
    <div className="bg-background">
      {/* Hero */}
      <section className="relative gradient-navy text-frost">
        <div className="grid-lines absolute inset-0 opacity-20" />
        <div className="relative mx-auto max-w-3xl px-5 pb-16 pt-36 sm:px-8 sm:pt-44">
          <Link
            to="/insights"
            className="inline-flex items-center gap-2 text-xs font-medium uppercase tracking-[0.2em] text-frost/70 transition hover:text-frost"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> All operating insights and case studies
          </Link>
          <span className="eyebrow on-dark mt-8 block">{post.category}, {post.read}</span>
          <h1 className="mt-4 font-serif text-4xl leading-[1.08] text-frost sm:text-5xl">
            {post.title}
          </h1>
          <p className="mt-3 text-sm text-frost/70">{postDateByline(post.slug)}</p>
          <p className="mt-6 text-lg leading-relaxed text-frost/80">{post.dek}</p>
          <div className="mt-10 flex items-center gap-4 border-t border-white/10 pt-6 text-sm text-frost/70">
            <div className="grid h-10 w-10 place-items-center rounded-full bg-frost/10 font-serif text-frost">
              A
            </div>
            <div>
              By <Link to="/about/adam-ayoub" className="font-medium text-frost underline decoration-white/30 underline-offset-4 transition hover:decoration-frost">Adam Ayoub</Link>
              <br />
              <span className="text-frost/80">Fractional COO, Ajility</span>
            </div>
          </div>
        </div>
      </section>

      {/* Summary video, only where src/data/postVideos.ts lists one */}
      {video ? (
        <section className="mx-auto max-w-7xl px-5 pt-14 sm:px-8">
          <ArticleVideo slug={post.slug} video={video} />
        </section>
      ) : null}

      {/* Lead photo, only where the post sets leadPhoto */}
      {post.leadPhoto && leadPhotos[post.leadPhoto.name] ? (
        <section className="mx-auto max-w-7xl px-5 pt-14 sm:px-8">
          <div className="mx-auto max-w-[760px] overflow-hidden rounded-2xl border border-border">
            <img
              src={leadPhotos[post.leadPhoto.name]}
              alt={post.leadPhoto.alt}
              width={1536}
              height={864}
              className="block aspect-video h-auto w-full object-cover"
            />
          </div>
        </section>
      ) : null}

      {/* Article */}
      <section className="mx-auto max-w-7xl px-5 py-20 sm:px-8">
        {post.slug === "working-partner-vs-silent-partner" ? <>
          <article className="article-body" dangerouslySetInnerHTML={{ __html: articleHtml.split("<!--partner-reward-chart-->")[0] }} />
          <div className="my-10"><PartnerRewardChart /></div>
          <article className="article-body" dangerouslySetInnerHTML={{ __html: articleHtml.split("<!--partner-reward-chart-->")[1]?.split("<!--partner-tool-callout-->")[0] ?? "" }} />
          <div className="mx-auto my-10 max-w-[760px]"><ToolCallout tool="partner-alignment-check" articleSlug={post.slug} /></div>
          <article className="article-body" dangerouslySetInnerHTML={{ __html: articleHtml.split("<!--partner-tool-callout-->")[1] ?? "" }} />
        </> : <article className="article-body" onClick={onArticleCalloutClick} dangerouslySetInnerHTML={{ __html: articleHtml }} />}
      </section>

      {/* CTA */}
      <section className="bg-midnight-deep">
        <div className="mx-auto max-w-4xl px-5 py-20 text-center text-frost sm:px-8">
          <h2 className="font-serif text-4xl leading-tight sm:text-5xl">
             Find the operating bottleneck in your business
          </h2>
          <p className="mx-auto mt-5 max-w-2xl text-frost/75">
             Tell Ajility what keeps returning to your desk. A 30-minute fit call is a structured conversation about where the constraint sits, not a pitch.
          </p>
          <BookFitCall
            placement={`insights_article_${post.slug}_footer`}
            buttonClassName="mt-8 inline-flex items-center gap-2 rounded-full bg-frost px-7 py-3.5 text-sm font-medium text-midnight-deep transition hover:bg-white"
            noteClassName="mt-2 text-sm font-medium text-frost/70 hover:text-frost"
          >
            Book a fit call <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
          </BookFitCall>
          <p className="mt-8 font-serif italic text-frost/80">
            "Strategy without execution is hallucination."
          </p>
        </div>
      </section>

      {insightToolCallouts[post.slug]?.length ? (
        <section className="mx-auto max-w-7xl px-5 pt-20 sm:px-8">
          <div className="mx-auto grid max-w-[760px] gap-4">
            {insightToolCallouts[post.slug]!.map((tool) => (
              <ToolCallout key={tool} tool={tool} articleSlug={post.slug} />
            ))}
          </div>
        </section>
      ) : null}

      {/* Related */}
      <section className="mx-auto max-w-7xl px-5 py-20 sm:px-8">
        <div className="mb-10 flex items-end justify-between">
          <div>
            <span className="eyebrow text-steel">Keep reading</span>
             <h2 className="mt-2 font-serif text-3xl text-foreground">More fractional COO insights</h2>
          </div>
          <Link to="/insights" className="text-sm font-medium text-midnight hover:underline">
            All operating insights and case studies
          </Link>
        </div>
        <div className="grid gap-8 md:grid-cols-3">
          {(post.slug === "working-partner-vs-silent-partner" ? [
            { slug: "partner-alignment", title: "Partner Alignment Review", category: "Service", dek: "Agree the six partnership areas with every partner.", to: "/services/partner-alignment" },
            { slug: "partner-alignment-check", title: "Partner Alignment Check", category: "Free tool", dek: "Where you and your business partners agree, and where you do not.", to: "/tools/partner-alignment-check" },
            { slug: "how-to-restructure-a-small-business-group-with-shared-owners", title: "How to restructure a small business group with shared owners", category: "Insight", dek: "Decide what the group is for before who reports to whom.", to: "/insights/how-to-restructure-a-small-business-group-with-shared-owners" },
          ] : related.map(p => ({ ...p, to: `/insights/${p.slug}` }))).map((p) => (
            <a
              key={p.slug}
              href={p.to}
              className="group flex flex-col card-panel card-interactive"
            >
              <span className="eyebrow text-steel">{p.category}</span>
              <h3 className="mt-3 font-serif text-xl leading-snug text-foreground">{p.title}</h3>
              <p className="mt-3 flex-1 text-sm text-muted-foreground">{p.dek}</p>
              <span className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-midnight transition group-hover:gap-3">
                {p.title} <ArrowUpRight className="h-4 w-4" />
              </span>
            </a>
          ))}
        </div>
      </section>
    </div>
  );
}
